import React, { useContext, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogModal, useHandleOpen } from '@engloba-tech/englobity';
import CloseIcon from '@material-ui/icons/Close';
import DoneIcon from '@material-ui/icons/Done';
import CloudDownloadOutlinedIcon from '@material-ui/icons/CloudDownloadOutlined';
import { NAME_SPACE } from 'i18n';
import { ViewTable, ButtonAdd, READER_REQUEST_TYPES } from 'shared';
import { ROLES, UserContext } from 'auth';
import { useReaderRequestsPaginated } from './useReaderRequestsPaginated';
import { useReaderRequestsBuildTable } from './useReaderRequestsBuildTable';
import { useListReaderRequestsStyles } from './listReaderRequests.styles';
import { FormCreateRequest } from './FormCreateRequest';
import { StepperDefectiveTagsRequest } from './StepperDefectiveTagsRequest';

export function ListReaderRequests() {
  const classes = useListReaderRequestsStyles();
  const $formCreateRequest = useRef(null);
  const $formProcessRequest = useRef(null);
  const {
    readerRequests,
    getReaderRequests,
    errorInfo,
    setErrorInfo,
    createRequest,
    processResponse,
    handleDownload,
    isCreating
  } = useReaderRequestsPaginated();
  const { t } = useTranslation(NAME_SPACE.READER_REQUESTS);
  const { rows, headCells } = useReaderRequestsBuildTable(readerRequests, t);
  const { user } = useContext(UserContext);
  const {
    isOpen: isCreateRequestOpen,
    handleClose: handleCloseCreateRequest,
    handleOpen: handleOpenAddCreateRequest
  } = useHandleOpen(false);
  const {
    isOpen: isProcessDefectiveTagsOpen,
    handleClose: handleCloseProcessDefectiveTags,
    handleOpen: handleOpenProcessDefectiveTags
  } = useHandleOpen(true);
  const [selectedRequestId, setSelectedRequestId] = useState();

  function composeActionButtons() {
    if (user?.role !== ROLES.PRODUCTION) {
      return [];
    }

    const actionButtons = [
      {
        condition: isAnySelected => !isAnySelected,
        cb: handleOpenAddCreateRequest,
        component: ButtonAdd
      }
    ];
    return actionButtons;
  }

  function handleModalAcceptCreateRequest() {
    $formCreateRequest.current.submit();
  }

  function handleModalCloseCreateRequest() {
    setErrorInfo({ message: '', detail: '' });
    handleCloseCreateRequest();
  }

  async function handleCreateRequestSubmit(request) {
    setErrorInfo({ message: '', detail: '' });
    const created = await createRequest(request);
    if (created) {
      handleCloseCreateRequest();
      if ((created.requestType & READER_REQUEST_TYPES.replaceDefectiveTags) > 0) {
        setSelectedRequestId(created.id);
        handleOpenProcessDefectiveTags();
      }
    }
  }
  function handleModalCloseProcessDefectiveTags() {
    setErrorInfo({ message: '', detail: '' });
    handleCloseProcessDefectiveTags();
  }

  async function handleProcessRequestSubmit(requestId) {
    setErrorInfo({ message: '', detail: '' });
    const result = await processResponse(requestId);
    if (result) {
      handleModalCloseProcessDefectiveTags();
      setSelectedRequestId();
    }
  }

  return (
    <>
      <div className={classes.request}>
        <ViewTable
          elevation
          spaced
          rows={rows}
          cells={headCells}
          defaultOrderBy={{ property: 'requestDate', direction: 'desc' }}
          mainKey={'requestDate'}
          onFetchData={getReaderRequests}
          totalRows={readerRequests.total}
          serverSidePaging
          emptyText={t('empty')}
          allowRowSelection={false}
          toolbar={{
            title: t('name'),
            actions: composeActionButtons()
          }}
          actions={[
            {
              condition: element => !element['responseFileUrl'],
              whenConditionTrue: null,
              title: t('actions.download'),
              name: 'download',
              properties: ['responseFileUrl'],
              label: 'download',
              cb: handleDownload,
              icon: <CloudDownloadOutlinedIcon />
            }
          ]}
        />
      </div>
      <DialogModal
        title={t('create.dialogTitle')}
        description=""
        fullWidth
        className={classes.modal}
        isOpen={isCreateRequestOpen}
        onClose={handleModalCloseCreateRequest}
        buttons={[
          {
            children: <CloseIcon />,
            text: t('common:actions.cancel'),
            type: 'secondary',
            onClick: handleModalCloseCreateRequest,
            className: classes.buttonDelete
          },
          {
            children: <DoneIcon />,
            text: t('common:actions.add'),
            type: 'primary',
            onClick: handleModalAcceptCreateRequest,
            className: classes.buttonAdd,
            disabled: isCreating
          }
        ]}
      >
        <FormCreateRequest formRef={$formCreateRequest} onSubmit={handleCreateRequestSubmit} errors={errorInfo} />
      </DialogModal>
      <DialogModal
        title={t('defectiveTagsReplacement.dialogTitle')}
        description=""
        fullWidth
        className={classes.modal}
        isOpen={selectedRequestId && isProcessDefectiveTagsOpen}
      >
        <StepperDefectiveTagsRequest
          formRef={$formProcessRequest}
          errors={errorInfo}
          requestId={selectedRequestId}
          onCancel={handleModalCloseProcessDefectiveTags}
          onSubmit={handleProcessRequestSubmit}
          isBusy={isCreating}
        />
      </DialogModal>
    </>
  );
}
